import { Link, useParams, useRouter, useSearchParams } from "@/lib/nav";
import { useEffect, useState } from "react";
import { BoardHeader, BoardShell, Loading, NotFoundBoard } from "@/components/Chrome";
import { SeatLegend, SeatMap } from "@/components/SeatMap";
import { clearPending, StepBar, usePendingSignup } from "@/components/StepBar";
import { TableList } from "@/components/TableList";
import { assignSeatByToken, signupGroup, takenSeatsOf, useFlightView, usePassView } from "@/lib/store";

/**
 * 選位頁。兩種進場方式：
 * 1. 報名流程第二步——可直接選 1～名額上限個位子；第 2 位起的同行者姓名在確認列填
 * 2. 已有登機證但還沒座位的人補選位：/flight/XX/seat?pass=<token>
 */
export default function SeatPage() {
  const { code } = useParams<{ code: string }>();
  const reseatToken = useSearchParams().get("pass");
  const view = useFlightView(code);
  const passView = usePassView(reseatToken);
  const router = useRouter();

  const flight = view?.flight;
  const pending = usePendingSignup(reseatToken ? undefined : flight?.code);

  const [selected, setSelected] = useState<string[]>([]);
  /** 第 i+2 個座位的同行者姓名（index 0 = 第二個座位） */
  const [companionNames, setCompanionNames] = useState<string[]>([]);
  /** 同行者的產業，index 與 companionNames 對齊。跟本人一樣是必填 */
  const [companionIndustries, setCompanionIndustries] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 點擊已入座的座位 → 顯示是誰（姓名＋產業）
  const [peek, setPeek] = useState<{ code: string; name: string; industry?: string } | null>(null);
  // 側邊清單點開的那一桌：清單展開它的座位，地圖同時捲過去並標示
  const [focusLabel, setFocusLabel] = useState<string | null>(null);
  // 完成選位後 pending 會被清掉，用這個旗標避免被誤判成「沒報名」而踢回櫃檯
  const [done, setDone] = useState(false);

  // 沒有進行中的報名、也不是補選位 → 回櫃檯重新開始
  const shouldBounce = !!flight && !reseatToken && !pending && !done;
  useEffect(() => {
    if (shouldBounce && flight) router.replace(`/flight/${flight.code}/checkin`);
  }, [shouldBounce, flight, router]);

  const loading = view === undefined || (!!reseatToken && passView === undefined);
  if (loading || shouldBounce) {
    return (
      <BoardShell wide>
        <BoardHeader />
        <Loading />
      </BoardShell>
    );
  }

  if (!view || !flight) {
    return (
      <BoardShell>
        <BoardHeader />
        <NotFoundBoard message={`找不到航班 ${code}`} />
      </BoardShell>
    );
  }

  const reseatAttendee = reseatToken ? passView?.attendee : undefined;
  if (
    reseatToken &&
    (!reseatAttendee || reseatAttendee.flightId !== flight.id || reseatAttendee.status !== "confirmed")
  ) {
    return (
      <BoardShell>
        <BoardHeader />
        <NotFoundBoard
          message="這張登機證無法在此活動選位（可能是候補票或已取消）。"
          backHref={`/flight/${flight.code}`}
          backLabel="返回活動頁"
        />
      </BoardShell>
    );
  }

  const taken = takenSeatsOf(view);
  const leadName = reseatAttendee?.name ?? pending?.name ?? "";
  const maxSeats = reseatAttendee ? 1 : Math.max(1, pending?.maxSeats ?? 1);

  const toggleSeat = (seatCode: string) => {
    setError(null);
    setSelected((prev) => {
      if (prev.includes(seatCode)) return prev.filter((s) => s !== seatCode);
      if (prev.length < maxSeats) return [...prev, seatCode];
      // 已選滿：換掉最後一個
      return [...prev.slice(0, maxSeats - 1), seatCode];
    });
  };

  const setAt = (
    set: React.Dispatch<React.SetStateAction<string[]>>,
    i: number,
    value: string,
  ) => {
    set((prev) => {
      const next = [...prev];
      while (next.length <= i) next.push("");
      next[i] = value;
      return next;
    });
  };
  const setCompanionName = (i: number, value: string) => setAt(setCompanionNames, i, value);
  const setCompanionIndustry = (i: number, value: string) =>
    setAt(setCompanionIndustries, i, value);

  // 同行者跟本人一樣要有姓名與產業 —— 產業會顯示在同桌名單上，
  // 少填的人到了現場就是那個「不知道是誰」的位子
  const companionsOk = selected
    .slice(1)
    .every((_, i) => (companionNames[i] ?? "").trim() && (companionIndustries[i] ?? "").trim());
  const canConfirm = selected.length >= 1 && companionsOk && !submitting;

  const confirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);

    if (pending) {
      const people = [
        { name: pending.name, industry: pending.industry },
        ...selected.slice(1).map((_, i) => ({
          name: companionNames[i].trim(),
          industry: companionIndustries[i].trim(),
        })),
      ];
      const res = await signupGroup({
        flightCode: flight.code,
        inviteId: pending.inviteId,
        people,
        note: pending.note,
        seats: selected,
      });
      if (!res.ok) {
        setSubmitting(false);
        if (res.reason === "seat-taken") {
          setError("有座位剛被別人選走了，請重新選擇。");
          setSelected([]);
        } else if (res.reason === "quota-exceeded") {
          setError("接龍名額不足（可能剛被使用），請回上一步確認。");
        } else if (res.reason === "not-enough-seats") {
          setError("剩餘座位不足，請減少選位數。");
        } else if (res.reason === "industry-required") {
          setError("每位同行者都要填產業——同桌的人會看到。");
        } else {
          setError("報名失敗，請回上一步重新開始。");
        }
        return;
      }
      setDone(true);
      clearPending(flight.code);
      router.push(`/pass/${res.attendees[0].passToken}?new=1`);
      return;
    }

    if (reseatAttendee && reseatToken) {
      const r = await assignSeatByToken(reseatToken, selected[0]);
      if (!r.ok) {
        setSubmitting(false);
        setError("這個座位剛被別人選走了，請再挑一個。");
        setSelected([]);
        return;
      }
      router.push(`/pass/${reseatAttendee.passToken}`);
    }
  };

  return (
    <BoardShell wide>
      <BoardHeader sub={`${flight.code} ${flight.title}`} />
      {pending && <StepBar current={2} />}

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight">選擇座位</h1>
          <p className="text-sub mt-1 text-[13px]">
            {maxSeats > 1
              ? `${leadName} · 名額 ${maxSeats} 位——選 1～${maxSeats} 個空位，同行者姓名在下方填`
              : `${leadName} · 選一個空位入座`}
            ；清單上看得到每桌坐了誰
          </p>
        </div>
        <SeatLegend />
      </div>

      {error && <p className="tint tint-bad mb-4">{error}</p>}
      {peek && (
        <div className="tint tint-accent mb-4 flex items-center justify-between gap-3">
          <span>
            <span className="font-semibold">{peek.code}</span> · {peek.name}
            {peek.industry && <span className="opacity-75">（{peek.industry}）</span>}
          </span>
          <button onClick={() => setPeek(null)} className="shrink-0 text-[13px] font-semibold opacity-60 hover:opacity-100">
            ✕
          </button>
        </div>
      )}

      {/*
        地圖在上、清單在下。

        手機上地圖釘住不動 —— 點清單裡的某一桌時，地圖就在原地跳過去並框起來，
        不用來回捲動找它。桌機不釘：畫面雖然高，但底部的確認列也是 sticky 的，
        上下兩條都固定住的話，中間留給清單的空間會小到幾乎點不到東西。
      */}
      <div className="flex flex-col gap-4">
        <div className="bg-canvas sticky top-0 z-10 pb-2 lg:static lg:z-auto lg:bg-transparent lg:pb-0">
          <SeatMap
            flight={flight}
            taken={taken}
            selected={selected}
            focusLabel={focusLabel}
            onFocusTable={setFocusLabel}
            onSelect={toggleSeat}
            onPeek={(seatCode, attendee) =>
              setPeek({ code: seatCode, name: attendee.name, industry: attendee.industry })
            }
          />
        </div>

        <TableList
          tables={flight.tables}
          taken={taken}
          selected={selected}
          focusLabel={focusLabel}
          onFocus={setFocusLabel}
          onSelect={toggleSeat}
        />
      </div>

      {/* 底部確認列 */}
      {/* 底部留白：確認列是 sticky 的，沒有這段最後一排桌子會被它蓋住 */}
      <div className="sticky bottom-3 mt-6 pb-4">
        <div className="card flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
            {selected.length === 0 ? (
              <div className="flex items-baseline gap-2.5">
                <span className="text-sub text-[13px] font-medium">座位</span>
                <span className="text-sub text-[24px] font-extrabold">—</span>
                {maxSeats > 1 && <span className="text-sub text-[12px]">可選 {maxSeats} 位</span>}
              </div>
            ) : (
              selected.map((s, i) => (
                <span key={s} className="flex items-center gap-1.5">
                  <span className="text-accent text-[18px] font-extrabold tabular-nums">{s}</span>
                  {i === 0 ? (
                    <span className="text-[13px] font-medium">{leadName}</span>
                  ) : (
                    <>
                      <input
                        value={companionNames[i - 1] ?? ""}
                        onChange={(e) => setCompanionName(i - 1, e.target.value)}
                        placeholder="同行者姓名"
                        maxLength={30}
                        className="field-input w-28 px-2.5 py-1.5 text-[13px]"
                        aria-label={`${s} 的同行者姓名`}
                      />
                      <input
                        value={companionIndustries[i - 1] ?? ""}
                        onChange={(e) => setCompanionIndustry(i - 1, e.target.value)}
                        placeholder="產業"
                        maxLength={20}
                        className="field-input w-24 px-2.5 py-1.5 text-[13px]"
                        aria-label={`${s} 的同行者產業`}
                      />
                    </>
                  )}
                </span>
              ))
            )}
            {selected.length > 0 && selected.length < maxSeats && (
              <span className="text-sub text-[12px]">還可再選 {maxSeats - selected.length} 位</span>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <Link
              href={pending ? `/flight/${flight.code}/checkin` : `/pass/${reseatToken}`}
              className="btn btn-secondary text-[14px]"
            >
              ← 上一步
            </Link>
            <button onClick={confirm} disabled={!canConfirm} className="btn btn-primary">
              CONFIRM · 確認選位
            </button>
          </div>
        </div>
      </div>
    </BoardShell>
  );
}
